import React from 'react';
import { Menu as MuiMenu, MenuProps as MuiMenuProps, useTheme } from '@mui/material';
import { useDropdownContext } from './Dropdown';

export interface DropdownMenuProps {
  /** The position of the menu */
  alignment?: 'left' | 'right' | 'match-width';

  /** A transform for potentially correcting the alignment */
  transform?: string;

  /** An optional minimum width to avoid breaking words to letters */
  minWidth?: string | number;

  /** Children elements (typically DropdownItems) */
  children?: React.ReactNode;
}

/**
 * DropdownMenu displays the menu content in a popover.
 *
 * This component maintains API compatibility with the original pounce DropdownMenu,
 * including support for animations and positioning.
 *
 * @example
 * <DropdownMenu alignment="right" minWidth="150px">
 *   <DropdownItem onSelect={() => {}}>Edit</DropdownItem>
 *   <DropdownItem onSelect={() => {}}>Delete</DropdownItem>
 * </DropdownMenu>
 */
export const DropdownMenu = React.forwardRef<HTMLDivElement, DropdownMenuProps>(
  function DropdownMenu({ alignment = 'left', transform, minWidth = '75px', children }, ref) {
    const { anchorEl, isOpen, handleClose } = useDropdownContext();
    const theme = useTheme();
    const { componentTokens } = theme.palette;

    // Map alignment to MUI anchorOrigin and transformOrigin
    const getAnchorOrigin = (): MuiMenuProps['anchorOrigin'] => {
      switch (alignment) {
        case 'left':
          return { vertical: 'bottom', horizontal: 'left' };
        case 'right':
          return { vertical: 'bottom', horizontal: 'right' };
        case 'match-width':
          return { vertical: 'bottom', horizontal: 'left' };
        default:
          return { vertical: 'bottom', horizontal: 'right' };
      }
    };

    const getTransformOrigin = (): MuiMenuProps['transformOrigin'] => {
      switch (alignment) {
        case 'left':
          return { vertical: 'top', horizontal: 'left' };
        case 'right':
          return { vertical: 'top', horizontal: 'right' };
        case 'match-width':
          return { vertical: 'top', horizontal: 'left' };
        default:
          return { vertical: 'top', horizontal: 'right' };
      }
    };

    return (
      <MuiMenu
        ref={ref}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={getAnchorOrigin()}
        transformOrigin={getTransformOrigin()}
        transitionDuration={250}
        disableAutoFocusItem
        slotProps={{
          paper: {
            sx: {
              backgroundColor: componentTokens?.selectMenuItems.color.item.background.containerBase,
              minWidth: alignment === 'match-width' && anchorEl ? anchorEl.offsetWidth : minWidth,
              transform,
            },
          },
        }}
      >
        {children}
      </MuiMenu>
    );
  },
);
